import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import Pager from "@/components/site/Pager";
import Reveal from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const PAGE_SIZE = 9;

export default function Blog() {
  useSEO({ title: "Career Blog", description: "Career guidance and salary insights for pharma professionals.", pageKey: "blog" });
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10) || 1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/blog", { params: { page, page_size: PAGE_SIZE } }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [page]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (page > 1) next.set("page", String(page));
    setParams(next, { replace: true });
  }, [page, setParams]);

  return (
    <>
      <PageHero
        overline="Career Blog"
        title="Career playbooks for pharma professionals."
        description="Interview prep, resume tactics, salary trends and industry know-how — written by recruiters who hire in pharma every day."
      />
      <section className="section-pad bg-slate-50">
        <div className="container-page">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((p, i) => (
              <Reveal key={p.id} variant="up" delay={Math.min(i * 80, 320)}>
                <Link to={`/blog/${p.slug}`} className="group block h-full overflow-hidden rounded-xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-md" data-testid={`blog-card-${p.slug}`}>
                {p.cover_image && (
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(p.published_at).toLocaleDateString()}
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900 group-hover:text-primary">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-slate-600">{p.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Read more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
                </Link>
              </Reveal>
            ))}
            {items.length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-500">No articles yet.</div>
            )}
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="blog-pager" />
        </div>
      </section>
    </>
  );
}
